import React from 'react';

/**
 * Button component with different variants
 * @param {Object} props - Component props
 * @param {boolean} props.primary - Primary button style
 * @param {boolean} props.secondary - Secondary button style
 * @param {boolean} props.tertiary - Tertiary button style
 * @param {boolean} props.strava - Strava-inspired orange button
 * @param {boolean} props.nike - Nike-inspired accent button
 * @param {boolean} props.danger - Danger button style
 * @param {boolean} props.small - Small size variant
 * @param {boolean} props.large - Large size variant
 * @param {boolean} props.rounded - Fully rounded button (pill)
 * @param {React.ReactNode} props.icon - Icon to display before button text
 * @param {React.ReactNode} props.iconRight - Icon to display after button text
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onClick - Click event handler
 * @param {React.ReactNode} props.children - Button content
 */
const Button = ({
  primary,
  secondary,
  tertiary,
  strava,
  nike,
  danger,
  small,
  large,
  rounded,
  icon,
  iconRight,
  disabled,
  className = '',
  onClick,
  children,
  ...rest
}) => {
  // Determine button style based on props
  let buttonStyle = '';

  if (primary) {
    buttonStyle = disabled
      ? 'bg-primary-400 text-white cursor-not-allowed'
      : 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white';
  } else if (secondary) {
    buttonStyle = disabled
      ? 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'
      : 'bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:active:bg-gray-500';
  } else if (tertiary) {
    buttonStyle = disabled
      ? 'text-gray-400 cursor-not-allowed'
      : 'text-gray-600 hover:text-gray-800 active:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 dark:active:text-white';
  } else if (strava) {
    buttonStyle = disabled
      ? 'bg-strava-400 text-white cursor-not-allowed'
      : 'bg-strava-500 hover:bg-strava-600 active:bg-strava-700 text-white';
  } else if (nike) {
    buttonStyle = disabled
      ? 'bg-nike-400 text-white cursor-not-allowed'
      : 'bg-nike-600 hover:bg-nike-700 active:bg-nike-800 text-white';
  } else if (danger) {
    buttonStyle = disabled
      ? 'bg-red-400 text-white cursor-not-allowed'
      : 'bg-red-500 hover:bg-red-600 active:bg-red-700 text-white';
  } else {
    // Default style
    buttonStyle = disabled
      ? 'bg-primary-400 text-white cursor-not-allowed'
      : 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white';
  }

  // Size styles
  let sizeStyle = 'px-4 py-2 text-sm'; // Default

  if (small) {
    sizeStyle = 'px-3 py-1 text-xs';
  } else if (large) {
    sizeStyle = 'px-6 py-3 text-base';
  }

  // Border styles
  const radiusStyle = rounded
    ? 'rounded-full'
    : 'rounded-lg';

  // Shadow & border
  const shadowStyle = tertiary
    ? ''
    : 'shadow-sm hover:shadow transition-shadow duration-200';

  // Typography
  const typographyStyle = 'font-medium tracking-wide';

  // Animation
  const animationStyle = 'transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]';

  return (
    <button
      className={`${buttonStyle} ${sizeStyle} ${radiusStyle}
        ${shadowStyle} ${typographyStyle} ${animationStyle}
        focus:outline-none focus:ring-2 focus:ring-offset-2
        ${primary ? 'focus:ring-primary-500' : ''}
        ${secondary ? 'focus:ring-gray-400' : ''}
        ${strava ? 'focus:ring-strava-500' : ''}
        ${nike ? 'focus:ring-nike-500' : ''}
        ${danger ? 'focus:ring-red-500' : ''}
        ${tertiary ? '' : 'border border-transparent'}
        ${icon || iconRight ? 'inline-flex items-center justify-center' : ''}
        ${className}`}
      disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {icon && <span className={`${children ? 'mr-2' : ''}`}>{icon}</span>}
      {children}
      {iconRight && <span className={`${children ? 'ml-2' : ''}`}>{iconRight}</span>}
    </button>
  );
};

export default Button;
